import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Send, 
  Bot,
  Sparkles,
  Heart,
  BookOpen,
  Brain,
  Smile,
  Bell,
  ChevronRight,
  Loader2,
  Leaf
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import LeaveMessageModal from './LeaveMessageModal';

const safeStorage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('Storage save failed:', e);
    }
  }
};

const QUICK_QUESTIONS = [
  { icon: Heart, text: '查看我的心情', color: 'text-primary-500 bg-primary-50' },
  { icon: Brain, text: '我最近很焦虑', color: 'text-sky-500 bg-sky-50' },
  { icon: BookOpen, text: '推荐心理工具', color: 'text-sage-500 bg-sage-50' },
  { icon: Smile, text: '聊聊我的状态', color: 'text-gold-500 bg-gold-50' }
];

const MAX_MESSAGES = 100;

const AIAssistant = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnreadReminders, setHasUnreadReminders] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [messages, setMessages] = useState(() => {
    const saved = safeStorage.get('meta_chat_history');
    if (saved && saved.length > 0) {
      const lastMsg = saved[saved.length - 1];
      if (lastMsg && lastMsg.timestamp) {
        const lastDate = new Date(lastMsg.timestamp).toDateString();
        const today = new Date().toDateString();
        if (lastDate === today) {
          return saved.slice(-MAX_MESSAGES);
        }
      }
    }
    return [{
      id: 1,
      type: 'bot',
      content: '你好！我是 Meta 🌿\n你的AI心理助手，有什么我可以帮你的吗？',
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date().toISOString()
    }];
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [remindersDismissed, setRemindersDismissed] = useState(() => {
    const dismissedDate = safeStorage.get('meta_reminders_dismissed_date', null);
    return dismissedDate === new Date().toDateString();
  });
  const [showLeaveMessage, setShowLeaveMessage] = useState(false);
  const [leaveMessageContext, setLeaveMessageContext] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sendLockRef = useRef(false);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      fetchReminders();
      fetchChatHistory();
    }
  }, [user]);
  
  useEffect(() => {
    let interval;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (user) {
          const localHistory = safeStorage.get('meta_chat_history');
          if (localHistory) {
            setMessages(localHistory.slice(-MAX_MESSAGES));
          }
          fetchReminders();
          interval = setInterval(fetchReminders, 5 * 60 * 1000);
        }
      } else {
        if (interval) clearInterval(interval);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    handleVisibilityChange();
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (interval) clearInterval(interval);
    };
  }, [user]);
  
  const fetchChatHistory = async () => {
    try {
      const localHistory = safeStorage.get('meta_chat_history');
      if (localHistory) {
        const today = new Date().toDateString();
        const recentMessages = localHistory.filter(m => 
          new Date(m.timestamp || Date.now()).toDateString() === today
        ).slice(-20);
        
        if (recentMessages.length > 0) {
          setMessages(recentMessages);
        }
      }
      
      const response = await api.get('/api/ai-assistant/chat-history');
      const history = response.data.history || [];
      
      if (history.length > 0) {
        const formattedMessages = history.slice(-20).map((msg, idx) => ({
          id: idx + 1,
          type: msg.type,
          content: msg.content,
          time: new Date(msg.created_at).toLocaleTimeString('zh-CN', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          timestamp: msg.created_at
        }));
        
        setMessages(formattedMessages);
        safeStorage.set('meta_chat_history', formattedMessages);
      }
    } catch (error) {
      console.error('获取聊天历史失败:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/ai-assistant/user-profile');
      setUserProfile(response.data);
      
      const hasChatHistory = messages.length > 1;
      
      if (!hasChatHistory && response.data.suggestions && response.data.suggestions.length > 0) {
        const topSuggestion = response.data.suggestions[0];
        const welcomeMsg = [{
          id: 1,
          type: 'bot',
          content: `你好 ${user.username}！我是 Meta 🌿\n\n${topSuggestion.message}`,
          suggestions: [topSuggestion.title, '查看我的状态', '还有什么建议？'],
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
          timestamp: new Date().toISOString()
        }];
        setMessages(welcomeMsg);
        safeStorage.set('meta_chat_history', welcomeMsg);
      }
    } catch (error) {
      console.error('获取用户画像失败:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get('/api/ai-assistant/reminders');
      const unreadReminders = response.data.reminders || [];
      setReminders(unreadReminders);
      setHasUnreadReminders(unreadReminders.length > 0 && !remindersDismissed);
    } catch (error) {
      console.error('获取提醒失败:', error);
    }
  };

  const markReminderRead = async (reminderId) => {
    try {
      await api.post(`/api/ai-assistant/reminders/${reminderId}/read`);
      setReminders(prev => {
        const updated = prev.filter(r => r.id !== reminderId);
        setHasUnreadReminders(updated.length > 0);
        return updated;
      });
    } catch (error) {
      console.error('标记提醒失败:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
        scrollToBottom();
      }, 100);
    }
  }, [isOpen]);

  const saveMessagesToStorage = (newMessages) => {
    safeStorage.set('meta_chat_history', newMessages.slice(-MAX_MESSAGES));
  };
  
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isTyping || sendLockRef.current) return;
    
    sendLockRef.current = true;
    const userMsg = inputValue.trim();
    const now = new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    const timestamp = new Date().toISOString();
    
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      content: userMsg,
      time: now,
      timestamp
    };
    
    const updatedMessages = [...messages, newUserMessage].slice(-MAX_MESSAGES);
    setMessages(updatedMessages);
    saveMessagesToStorage(updatedMessages);
    
    setInputValue('');
    setIsTyping(true);
    
    try {
      const response = await api.post('/api/ai-assistant/chat', {
        message: userMsg,
        context: { userProfile }
      });
      
      setIsTyping(false);
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.data.content,
        suggestions: response.data.suggestions,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        timestamp: new Date().toISOString()
      };
      
      const finalMessages = [...updatedMessages, botMessage].slice(-MAX_MESSAGES);
      setMessages(finalMessages);
      saveMessagesToStorage(finalMessages);
      
    } catch (error) {
      console.error('AI对话失败:', error);
      setIsTyping(false);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: '抱歉，我暂时遇到了一些问题。请稍后再试，或者你可以直接浏览我们的心理工具页面。',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        timestamp: new Date().toISOString()
      };
      
      const finalMessages = [...updatedMessages, errorMessage].slice(-MAX_MESSAGES);
      setMessages(finalMessages);
      saveMessagesToStorage(finalMessages);
    } finally {
      sendLockRef.current = false;
    }
  }, [inputValue, isTyping, userProfile, messages]);

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.includes('记录心情')) {
      navigate('/mood-tracker');
      setIsOpen(false);
      return;
    }
    if (suggestion.includes('工具') || suggestion.includes('练习')) {
      navigate('/tools');
      setIsOpen(false);
      return;
    }
    if (suggestion.includes('测评')) {
      navigate('/assessments');
      setIsOpen(false);
      return;
    }
    if (suggestion.includes('留言给咨询师') || suggestion.includes('咨询师')) {
      const lastUserMessage = [...messages].reverse().find(m => m.type === 'user');
      const lastBotMessage = [...messages].reverse().find(m => m.type === 'bot');
      setLeaveMessageContext(`用户问题：${lastUserMessage?.content || ''}\n\nAI回复：${lastBotMessage?.content?.substring(0, 200) || ''}`);
      setShowLeaveMessage(true);
      return;
    }
    
    setInputValue(suggestion);
    setTimeout(() => handleSend(), 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReminderClick = (reminder) => {
    if (reminder.action) {
      navigate(reminder.action);
      setIsOpen(false);
    }
    markReminderRead(reminder.id);
  };

  return (
    <>
      <LeaveMessageModal
        isOpen={showLeaveMessage}
        onClose={() => setShowLeaveMessage(false)}
        initialMessage=""
        context={leaveMessageContext}
      />
      
      {hasUnreadReminders && !isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-72 max-w-[calc(100vw-3rem)] animate-slide-up">
          <div className="bg-white rounded-3xl shadow-soft-lg border border-primary-100 overflow-hidden">
            <div className="bg-gradient-to-r from-primary-500 to-primary-400 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center text-white">
                <Bell className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Meta 提醒</span>
              </div>
              <button 
                onClick={() => {
                  setHasUnreadReminders(false);
                  setRemindersDismissed(true);
                  safeStorage.set('meta_reminders_dismissed_date', new Date().toDateString());
                }}
                className="text-white/80 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
              {reminders.slice(0, 3).map((reminder) => (
                <div 
                  key={reminder.id}
                  onClick={() => handleReminderClick(reminder)}
                  className="flex items-center p-2.5 bg-slate-50 rounded-xl cursor-pointer hover:bg-primary-50 transition-colors"
                >
                  <div className={`w-2 h-2 rounded-full mr-2.5 ${
                    reminder.priority === 'high' ? 'bg-red-400' : 'bg-gold-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{reminder.title}</p>
                    <p className="text-xs text-slate-500 truncate">{reminder.message}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 悬浮按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-glow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-600 rotate-90 scale-0' 
            : 'bg-gradient-to-br from-primary-500 to-primary-400 hover:shadow-glow animate-bounce-slow'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <div className="relative flex items-center justify-center">
            <Bot className="w-7 h-7 text-white" />
            {hasUnreadReminders && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full border-2 border-white animate-pulse" />
            )}
            {!hasUnreadReminders && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-sage-400 rounded-full border-2 border-white" />
            )}
          </div>
        )}
      </button>

      {isOpen && (
        <button
          onClick={() => setIsOpen(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-slate-500 text-white shadow-soft-lg hover:bg-slate-600 transition-all"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-40 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-3xl shadow-soft-xl overflow-hidden border border-slate-100 animate-slide-up"
          style={{ maxHeight: 'calc(100vh - 8rem)' }}
        >
          {/* 头部 */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-400 px-4 py-3.5">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-sage-400 rounded-full border-2 border-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">Meta</h3>
                <p className="text-white/80 text-xs flex items-center">
                  <Sparkles className="w-3 h-3 mr-1" />
                  你的AI心理助手
                </p>
              </div>
              <button
                onClick={() => setShowReminders(!showReminders)}
                className="relative p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
              >
                <Bell className="w-5 h-5" />
                {hasUnreadReminders && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-400 rounded-full" />
                )}
              </button>
            </div>
          </div>

          {/* 提醒面板 */}
          {showReminders && reminders.length > 0 && (
            <div className="bg-gold-50/70 border-b border-gold-100 p-3 max-h-40 overflow-y-auto">
              <p className="text-xs text-gold-600 font-medium mb-2 flex items-center gap-1">
                <Leaf className="w-3 h-3" />
                待办提醒
              </p>
              {reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  onClick={() => handleReminderClick(reminder)}
                  className="flex items-center p-2.5 bg-white rounded-xl mb-2 cursor-pointer hover:bg-gold-50 transition-colors shadow-soft"
                >
                  <div className={`w-2 h-2 rounded-full mr-2.5 ${
                    reminder.priority === 'high' ? 'bg-red-400' : 'bg-gold-400'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{reminder.title}</p>
                    <p className="text-xs text-slate-500">{reminder.message}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          )}

          {/* 消息区域 */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-cream-50/50 to-white">
            {messages.map((msg) => (
              <div key={msg.id}>
                <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.type === 'bot' && (
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-400 rounded-full flex items-center justify-center flex-shrink-0 mr-2.5 shadow-soft">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div 
                    className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.type === 'user'
                        ? 'bg-gradient-to-br from-primary-500 to-primary-400 text-white rounded-tr-sm shadow-soft'
                        : 'bg-white text-slate-600 shadow-soft rounded-tl-sm border border-slate-100'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
                
                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 ml-10.5">
                    {msg.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-3.5 py-1.5 bg-white border border-primary-200 text-primary-500 text-xs rounded-full hover:bg-primary-50 hover:border-primary-300 transition-colors shadow-soft"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className={`text-[10px] text-slate-400 mt-1.5 ${msg.type === 'user' ? 'text-right' : 'ml-10.5'}`}>
                  {msg.time}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-400 rounded-full flex items-center justify-center flex-shrink-0 mr-2.5 shadow-soft">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-soft border border-slate-100">
                  <div className="flex space-x-1.5">
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* 快捷问题 */}
          {messages.length === 1 && (
            <div className="px-4 py-3 bg-white border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-2.5">你可以这样问我：</p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_QUESTIONS.map((q, idx) => {
                  const Icon = q.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(q.text)}
                      className={`flex items-center space-x-2 p-2.5 rounded-xl text-xs text-left transition-all hover:shadow-soft ${q.color}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{q.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 输入区域 */}
          <div className="p-3.5 bg-white border-t border-slate-100">
            <div className="flex items-end space-x-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="输入消息..."
                rows={1}
                className="flex-1 px-4 py-2.5 bg-slate-100 border-0 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 placeholder:text-slate-400 resize-none min-h-[40px] max-h-[120px]"
                style={{ height: 'auto' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isTyping}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                  inputValue.trim() && !isTyping
                    ? 'bg-gradient-to-br from-primary-500 to-primary-400 text-white hover:shadow-glow'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-2">
              Meta 仅供参考，如有严重心理问题请寻求专业帮助
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
